import type { FC, ReactNode } from "react";
import { createContext, useMemo } from "react";

import { STEPS_COUNT } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/LLMSetupConst";

import type { IFormStepContextValue, IStepConfig, IStepData, IStepError, IStepHandlers } from "../types";

export const FormStepContext = createContext<IFormStepContextValue | undefined>(undefined);

interface IFormStepProviderProps {
    children: ReactNode;
    currentStep: number;
    currentStepConfig: IStepConfig;
    stepData: IStepData;
    stepHandlers: IStepHandlers;
    stepErrors: IStepError[];
    showStepErrors: boolean;
    dismissStepErrors: () => void;
    goToNext: () => void;
    goToPrevious: () => void;
    goToStep: (step: number) => void;
}

export const FormStepProvider = ({
    children,
    currentStep,
    currentStepConfig,
    stepData,
    stepHandlers,
    stepErrors,
    showStepErrors,
    dismissStepErrors,
    goToNext,
    goToPrevious,
    goToStep,
}: IFormStepProviderProps) => {
    const stepFooterComponent = useMemo<FC | null>(() => {
        if (!currentStepConfig?.renderFooter) {
            return null;
        }
        return () => currentStepConfig.renderFooter(stepData);
    }, [currentStepConfig, stepData]);

    const value: IFormStepContextValue = {
        currentStep,
        isFirstStep: currentStep === 1,
        isLastStep: currentStep === STEPS_COUNT,
        goToNext,
        goToPrevious,
        goToStep,
        stepComponent: currentStepConfig.component,
        stepFooterComponent,
        stepData,
        stepHandlers,
        stepErrors,
        showStepErrors,
        dismissStepErrors,
    };

    return <FormStepContext.Provider value={value}>{children}</FormStepContext.Provider>;
};
